/**
 * Lives that repeat: a recurring meeting people join once and are in for
 * every session. Designed in `prototype/recurring-lives.html` (approved 19 Sep
 * 2026), with the calendar-style Repeats picker added on 26 Oct 2026. This is
 * the one place a series' rule turns into dates and words, so the server, the
 * calendar files, the page, the emails and both apps always agree.
 *
 * A series repeats on the host's clock: 7:00 PM stays 7:00 PM in the host's
 * zone through daylight saving, so people in other zones may see it move by
 * an hour. Every date here is a wall date in the host's zone ("2026-09-24").
 *
 * A session keeps its place in the series (`seq`, 1 for the first) whatever
 * happens to it, so a skipped or missed session still counts toward "after 10
 * sessions", the way a calendar counts an excluded date. When the host changes
 * the series, the new rule starts at the next session: `fromSeq` is that
 * session's place and `firstDate` its date.
 *
 * A rule's weeks start on Sunday. That only matters for "every 2 weeks" and
 * more on several days, and it is fixed here (and in the calendar file's
 * WKST) so a rule never depends on who reads it. The order the picker lists
 * the days in follows the browser and is display only.
 */
import { labelParts, roomOpensAt, wallClock, wallTimeToUtc } from "./live-time";

export type SeriesUnit = "day" | "week" | "month" | "year";

export const SERIES_UNITS: readonly SeriesUnit[] = ["day", "week", "month", "year"];

/** For a monthly rhythm: the same date, or the same weekday of the month (the last one for a fifth week). */
export type SeriesMonthBy = "day" | "nth";

/** When a series stops: never, after a number of sessions (the first counts), or on a date (inclusive). */
export type SeriesEnds = { kind: "never" } | { kind: "after"; count: number } | { kind: "on"; date: string };

/**
 * How a live repeats: every `every` days, weeks, months or years. For weeks,
 * `weekdays` says which (0 is Sunday; several sessions in one week are
 * allowed); for months, `monthBy` says the same date or the same weekday as the
 * first session (a 31st uses a shorter month's last day); for years, the same
 * date (29 February uses the 28th). "after" counts every session of the
 * series, the ones before a change too.
 */
export interface SeriesPattern {
  unit: SeriesUnit;
  every: number;
  /** Only for weeks; empty otherwise. */
  weekdays: number[];
  monthBy: SeriesMonthBy;
  ends: SeriesEnds;
}

/** A series' rule, as the host set it. */
export interface SeriesRule extends SeriesPattern {
  /** The date of session `fromSeq`, host clock, YYYY-MM-DD. */
  firstDate: string;
  /** The session the rule starts at: 1 for a new series, later once the host changed it. */
  fromSeq: number;
  /** Start time, host clock, HH:MM (24 hour). */
  time: string;
  /** IANA, e.g. Asia/Kolkata. */
  timezone: string;
  lengthMin: number;
}

/** What turns a first date into the dates of the sessions after it. */
type SeriesShape = Pick<SeriesRule, "unit" | "every" | "weekdays" | "monthBy" | "firstDate" | "fromSeq" | "ends">;

/** Sessions kept planned ahead for a series with no end; the cron tops them up. */
export const SERIES_AHEAD = 12;
/** The most sessions a series with an end may have. */
export const SERIES_MAX_COUNT = 104;
/** The most "every" a rhythm may say: every 99 days, weeks, months or years. */
export const SERIES_EVERY_MAX = 99;
/** The longest a session's topic may be. */
export const SESSION_TOPIC_MAX = 120;

/** One planned session: its place in the series, its wall date and its instant. */
interface SeriesSlot {
  seq: number;
  /** Host clock, YYYY-MM-DD. */
  date: string;
  /** UTC ISO. */
  startsAt: string;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const NTH_WORD = ["", "first", "second", "third", "fourth", "last"];
const NTH_SHORT = ["", "1st", "2nd", "3rd", "4th", "last"];
const WEEKDAYS = [1, 2, 3, 4, 5];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

function parts(date: string): { y: number; m: number; d: number } | null {
  const hit = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!hit) return null;
  const [y, m, d] = [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  const check = new Date(Date.UTC(y, m - 1, d));
  return check.getUTCMonth() === m - 1 && check.getUTCDate() === d ? { y, m, d } : null;
}

/** Day of the week of a wall date, 0 = Sunday. */
export function weekdayOf(date: string): number {
  const p = parts(date);
  return p ? new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay() : 0;
}

/** The name of a weekday, 0 = Sunday. */
export function dayName(weekday: number): string {
  return DAYS[weekday] ?? "";
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Which of the month's weekdays a date is: 1 to 4, or 5 for one in the fifth week (read as "last"). */
function nthOfMonth(date: string): number {
  const p = parts(date);
  return p ? Math.min(5, Math.ceil(p.d / 7)) : 1;
}

/** "fourth", or "last" for the fifth week: which weekday of its month a date is, in words. */
export function nthWord(date: string): string {
  return NTH_WORD[nthOfMonth(date)];
}

/** A wall date moved by whole days. */
export function addDays(date: string, n: number): string {
  const p = parts(date);
  if (!p) return date;
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d + n));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** The nth weekday of a month; 5 means the last one. */
function nthWeekdayIn(y: number, m: number, weekday: number, nth: number): string {
  if (nth >= 5) {
    const last = daysInMonth(y, m);
    const lastDow = new Date(Date.UTC(y, m - 1, last)).getUTCDay();
    return ymd(y, m, last - ((lastDow - weekday + 7) % 7));
  }
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  return ymd(y, m, 1 + ((weekday - firstDow + 7) % 7) + (nth - 1) * 7);
}

/** Days into the rule's week, which starts on Sunday. */
const weekOffset = (weekday: number) => weekday % 7;

/** The pattern's weekdays, once each, in the order the rule's week runs. */
function weekdaysInOrder(weekdays: readonly number[]): number[] {
  return [...new Set(weekdays)].toSorted((a, b) => weekOffset(a) - weekOffset(b));
}

/**
 * The first date on or after `date` the pattern puts a session on. Only a
 * weekly pattern can miss the day picked (Tuesday asked, Thursday only), and
 * then the series starts on its first Thursday.
 */
export function firstOn(pattern: Pick<SeriesPattern, "unit" | "every" | "weekdays">, date: string): string {
  const days = weekdaysInOrder(pattern.weekdays);
  if (pattern.unit !== "week" || days.length === 0 || !parts(date)) return date;
  const start = addDays(date, -weekOffset(weekdayOf(date)));
  for (let w = 0; w < 2; w++) {
    for (const wd of days) {
      const at = addDays(start, 7 * pattern.every * w + weekOffset(wd));
      if (at >= date) return at;
    }
  }
  return date;
}

/** The wall date the rule gives session `seq`, before any move. Sessions before `fromSeq` are not the rule's. */
export function slotDate(rule: SeriesShape, seq: number): string {
  const first = parts(rule.firstDate);
  const k = seq - rule.fromSeq;
  if (!first || k <= 0) return rule.firstDate;
  switch (rule.unit) {
    case "day":
      return addDays(rule.firstDate, rule.every * k);
    case "week": {
      const days = weekdaysInOrder(rule.weekdays);
      if (days.length === 0) return addDays(rule.firstDate, 7 * rule.every * k);
      const own = weekOffset(weekdayOf(rule.firstDate));
      const place = days.filter((d) => weekOffset(d) < own).length + k;
      const cycle = Math.floor(place / days.length);
      const start = addDays(rule.firstDate, -own);
      return addDays(start, 7 * rule.every * cycle + weekOffset(days[place % days.length]));
    }
    case "month": {
      const total = first.m - 1 + rule.every * k;
      const y = first.y + Math.floor(total / 12);
      const m = (total % 12) + 1;
      return rule.monthBy === "nth"
        ? nthWeekdayIn(y, m, weekdayOf(rule.firstDate), nthOfMonth(rule.firstDate))
        : ymd(y, m, Math.min(first.d, daysInMonth(y, m)));
    }
    case "year": {
      const y = first.y + rule.every * k;
      return ymd(y, first.m, Math.min(first.d, daysInMonth(y, first.m)));
    }
  }
}

/** The instant the rule gives session `seq` (UTC ISO), or null for a zone that cannot be read. */
export function slotStartsAt(rule: SeriesRule, seq: number): string | null {
  return wallTimeToUtc(slotDate(rule, seq), rule.time, rule.timezone);
}

/**
 * The place of the series' last session, or null for one that never ends.
 * An end date before any of the rule's sessions leaves the last one before it.
 */
export function seriesTotal(rule: SeriesShape): number | null {
  if (rule.ends.kind === "never") return null;
  if (rule.ends.kind === "after") return Math.max(1, Math.floor(rule.ends.count));
  let last = rule.fromSeq - 1;
  while (last < SERIES_MAX_COUNT + 1 && slotDate(rule, last + 1) <= rule.ends.date) last++;
  return Math.max(1, last);
}

/** The rule's sessions from `startSeq` on, at most `count`, stopping where the series ends. */
export function seriesSlots(rule: SeriesRule, startSeq: number, count: number): SeriesSlot[] {
  const total = seriesTotal(rule);
  const out: SeriesSlot[] = [];
  for (let seq = Math.max(rule.fromSeq, startSeq); out.length < count; seq++) {
    if (total !== null && seq > total) break;
    const startsAt = slotStartsAt(rule, seq);
    if (!startsAt) break;
    out.push({ seq, date: slotDate(rule, seq), startsAt });
  }
  return out;
}

/**
 * The dates a pattern gives from a picked day, at most `limit`, stopping where
 * it ends: what the host's picker lists under "Next". The server plans the
 * sessions from this same rule.
 */
export function patternDates(pattern: SeriesPattern, date: string, limit: number, fromSeq = 1): string[] {
  const rule = { ...pattern, firstDate: firstOn(pattern, date), fromSeq };
  const last = seriesTotal(rule) ?? Number.POSITIVE_INFINITY;
  const out: string[] = [];
  for (let seq = fromSeq; out.length < limit && seq <= last; seq++) out.push(slotDate(rule, seq));
  return out;
}

const sameDays = (a: readonly number[], b: readonly number[]) => {
  const x = weekdaysInOrder(a);
  const y = weekdaysInOrder(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

/** Does a rule repeat every week on one day: the shape its page and emails say "every Thursday" for. */
export function weeklyOnOneDay(rule: Pick<SeriesPattern, "unit" | "every" | "weekdays">): boolean {
  return rule.unit === "week" && rule.every === 1 && weekdaysInOrder(rule.weekdays).length === 1;
}

/** Two patterns put sessions on the same days, whatever they end on. */
export function samePattern(a: SeriesPattern, b: SeriesPattern): boolean {
  return (
    a.unit === b.unit &&
    a.every === b.every &&
    (a.unit !== "week" || sameDays(a.weekdays, b.weekdays)) &&
    (a.unit !== "month" || a.monthBy === b.monthBy)
  );
}

// --- the words ---------------------------------------------------------
function list(words: readonly string[]): string {
  return words.length < 2 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

/** "Tue Oct 27": a day in a row of dates, with no comma to read as a list. */
export function wallDayWords(date: string): string {
  const p = parts(date);
  return p ? `${DAYS[weekdayOf(date)].slice(0, 3)} ${MONTHS[p.m - 1].slice(0, 3)} ${p.d}` : "";
}

/** "October 27", with the year when it is not `first`'s. */
function longDate(date: string, first: string): string {
  const p = parts(date);
  if (!p) return "";
  return `${MONTHS[p.m - 1]} ${p.d}${p.y === parts(first)?.y ? "" : `, ${p.y}`}`;
}

/** The rhythm in words, for the page and the Room: "Every Thursday", "Every Tuesday and Thursday", "Every month on the fourth Thursday". */
export function rhythmLabel(rule: Pick<SeriesRule, "unit" | "every" | "weekdays" | "monthBy" | "firstDate">): string {
  const n = rule.every;
  const p = parts(rule.firstDate);
  switch (rule.unit) {
    case "day":
      return n === 1 ? "Every day" : `Every ${n} days`;
    case "week": {
      const days = weekdaysInOrder(rule.weekdays);
      if (n === 1 && days.length === 7) return "Every day";
      if (n === 1 && sameDays(days, WEEKDAYS)) return "Every weekday";
      const names = list(days.map((d) => DAYS[d]));
      if (n === 1) return `Every ${names}`;
      return `${n === 2 ? "Every other week" : `Every ${n} weeks`} on ${names}`;
    }
    case "month": {
      const every = n === 1 ? "Every month" : `Every ${n} months`;
      return rule.monthBy === "nth"
        ? `${every} on the ${nthWord(rule.firstDate)} ${DAYS[weekdayOf(rule.firstDate)]}`
        : `${every} on day ${p?.d ?? 1}`;
    }
    case "year":
      return `${n === 1 ? "Every year" : `Every ${n} years`} on ${MONTHS[(p?.m ?? 1) - 1]} ${p?.d ?? 1}`;
  }
}

/** The end as a tail for a sentence: ", until December 15", ", for 10 sessions", or nothing. */
export function endsTail(ends: SeriesEnds, firstDate: string): string {
  if (ends.kind === "on") return `, until ${longDate(ends.date, firstDate)}`;
  if (ends.kind === "after") return `, for ${ends.count} sessions`;
  return "";
}

/** "6:00 PM ET": a start time with its zone, as the rest of a live's labels say it. */
export function clockWords(date: string, time: string, timezone: string): string {
  const at = wallTimeToUtc(date, time, timezone);
  const lp = at ? labelParts(at, timezone, "short") : null;
  return lp ? `${lp.time} ${lp.zone}` : time;
}

function timeWords(rule: SeriesRule): string {
  return clockWords(rule.firstDate, rule.time, rule.timezone);
}

/** "Every Thursday · 7:00 PM India Time": the time line on the page, the board and the emails. */
export function seriesLongLabel(rule: SeriesRule): string {
  return `${rhythmLabel(rule)} · ${timeWords(rule)}`;
}

/**
 * The series in a few words, for chips and rows: "Every Thu · 7:00 PM India
 * Time", "Every Tue, Thu · …", "Every 2 weeks · Thu · …", "Every month · 4th
 * Thu · …", "Every month · day 24 · …", "Every year · Sep 24 · …".
 */
export function seriesChip(rule: SeriesRule): string {
  const p = parts(rule.firstDate);
  const day3 = DAYS[weekdayOf(rule.firstDate)].slice(0, 3);
  const time = timeWords(rule);
  const n = rule.every;
  switch (rule.unit) {
    case "day":
      return `${n === 1 ? "Every day" : `Every ${n} days`} · ${time}`;
    case "week": {
      const days = weekdaysInOrder(rule.weekdays);
      const short = days.map((d) => DAYS[d].slice(0, 3)).join(", ");
      if (n > 1) return `Every ${n} weeks · ${short} · ${time}`;
      const rhythm = rhythmLabel(rule);
      return `${rhythm === "Every day" || rhythm === "Every weekday" ? rhythm : `Every ${short}`} · ${time}`;
    }
    case "month": {
      const every = n === 1 ? "Every month" : `Every ${n} months`;
      return rule.monthBy === "nth"
        ? `${every} · ${NTH_SHORT[nthOfMonth(rule.firstDate)]} ${day3} · ${time}`
        : `${every} · day ${p?.d ?? 1} · ${time}`;
    }
    case "year":
      return `${n === 1 ? "Every year" : `Every ${n} years`} · ${MONTHS[(p?.m ?? 1) - 1].slice(0, 3)} ${p?.d ?? 1} · ${time}`;
  }
}

/**
 * "10 sessions, until Nov 26", "until Dec 31", or "no end". Given the
 * sessions, "until" is the day the last one really is, when the host moved it.
 */
export function endsLabel(rule: SeriesRule, sessions: ReadonlyArray<{ seq: number; startsAt: string }> = []): string {
  const total = seriesTotal(rule);
  if (total === null) return "no end";
  const moved = sessions.find((s) => s.seq === total);
  const last = parts((moved && wallClock(moved.startsAt, rule.timezone)?.date) || slotDate(rule, total));
  const until = last ? `${MONTHS[last.m - 1].slice(0, 3)} ${last.d}` : "";
  return rule.ends.kind === "after" ? `${total} sessions, until ${until}` : `until ${until}`;
}

const BYDAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

/** An instant as an iCalendar UTC value: 20261215T045900Z. */
const utcStamp = (iso: string) => iso.replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");

/**
 * The rule as an iCalendar RRULE value (RFC 5545 section 3.3.10, read 26 Oct
 * 2026: FREQ and INTERVAL, BYDAY with an ordinal for the Nth weekday of a
 * month, BYMONTHDAY, BYSETPOS and WKST, and COUNT or an UNTIL in UTC), counted
 * from `fromSeq`, for the series' calendar entry and Google's `recur`
 * parameter. Moved and skipped sessions are the caller's EXDATE and
 * RECURRENCE-ID entries. `BYSETPOS=1` over the date and the month's last day
 * is how a rule says "the 31st, or the last day of a shorter month".
 */
export function rruleOf(rule: SeriesRule): string {
  const p = parts(rule.firstDate) ?? { y: 1970, m: 1, d: 1 };
  const interval = rule.every > 1 ? `;INTERVAL=${rule.every}` : "";
  const monthDay = p.d > 28 ? `BYMONTHDAY=${p.d},-1;BYSETPOS=1` : `BYMONTHDAY=${p.d}`;
  let base: string;
  switch (rule.unit) {
    case "day":
      base = `FREQ=DAILY${interval}`;
      break;
    case "week": {
      const days = weekdaysInOrder(rule.weekdays).map((d) => BYDAY[d]);
      // Why WKST: with an interval over several days, the week a day belongs to decides which weeks run.
      base = `FREQ=WEEKLY${interval}${rule.every > 1 ? ";WKST=SU" : ""};BYDAY=${days.join(",")}`;
      break;
    }
    case "month": {
      const nth = nthOfMonth(rule.firstDate);
      const day = `BYDAY=${nth >= 5 ? -1 : nth}${BYDAY[weekdayOf(rule.firstDate)]}`;
      base = `FREQ=MONTHLY${interval};${rule.monthBy === "nth" ? day : monthDay}`;
      break;
    }
    case "year":
      base = `FREQ=YEARLY${interval};BYMONTH=${p.m};${monthDay}`;
      break;
  }
  if (rule.ends.kind === "on") {
    const until = wallTimeToUtc(rule.ends.date, "23:59", rule.timezone);
    if (until) return `${base};UNTIL=${utcStamp(until)}`;
  }
  const total = seriesTotal(rule);
  return total === null ? base : `${base};COUNT=${Math.max(1, total - rule.fromSeq + 1)}`;
}

/** What the current-session rule reads of a session. */
interface SessionPlace {
  seq: number;
  state: "scheduled" | "live" | "ended" | "skipped" | "missed";
  /** UTC ISO. */
  startsAt: string;
}

/** A session whose time has come and gone: it ran, was skipped, or nobody went live. */
function hasRun(s: SessionPlace): boolean {
  return s.state === "ended" || s.state === "skipped" || s.state === "missed";
}

/**
 * Who is asking which session a live is on: the host getting ready for the
 * next one, a person with a seat, or a person who left the series (the
 * sessions to come are no longer theirs).
 */
export type SessionView = "host" | "person" | "left";

/**
 * The session a live is on now: the one its board, studio, webinar page and
 * seat link open. The one that is live; else, for a person, the next one once
 * its room has opened (ten minutes before); else the latest that ended, whose
 * replay is what a person wants between sessions; else the next one; else the
 * last. The host skips the latest ended one, because the next is what they
 * get ready for. A one-off's one session is always its current one.
 */
export function currentSession<T extends SessionPlace>(sessions: readonly T[], nowMs: number, view: SessionView): T | null {
  const bySeq = sessions.toSorted((a, b) => a.seq - b.seq);
  const mine = view === "left" ? bySeq.filter(hasRun) : bySeq;
  const live = mine.find((s) => s.state === "live");
  if (live) return live;
  const next = mine.find((s) => s.state === "scheduled") ?? null;
  const opens = next ? Date.parse(roomOpensAt(next.startsAt) ?? next.startsAt) : Number.NaN;
  if (next && view === "person" && nowMs >= opens) return next;
  if (next && view === "host") return next;
  const ended = mine.filter((s) => s.state === "ended").at(-1);
  return ended ?? next ?? mine.at(-1) ?? null;
}


/** What is wrong with a pattern for a first session on `date`, in the host's words; null when it is fine. */
export function patternProblem(p: SeriesPattern, date: string, fromSeq = 1): string | null {
  if (!SERIES_UNITS.includes(p.unit)) return "Pick how often it repeats.";
  if (!Number.isInteger(p.every) || p.every < 1 || p.every > SERIES_EVERY_MAX) return `Repeat every 1 to ${SERIES_EVERY_MAX}.`;
  if (p.unit === "week" && !(Array.isArray(p.weekdays) && p.weekdays.length > 0 && p.weekdays.every((d) => Number.isInteger(d) && d >= 0 && d <= 6))) {
    return "Pick at least one day.";
  }
  if (p.unit === "month" && p.monthBy !== "day" && p.monthBy !== "nth") return "Pick which day of the month.";
  if (p.ends.kind === "after") {
    const least = Math.max(2, fromSeq);
    if (!Number.isInteger(p.ends.count) || p.ends.count < least || p.ends.count > SERIES_MAX_COUNT) {
      return `A series runs for ${least} to ${SERIES_MAX_COUNT} sessions.`;
    }
  }
  if (p.ends.kind === "on") {
    const first = firstOn(p, date);
    if (!parts(p.ends.date)) return "Pick the date it ends.";
    if (p.ends.date < first || (fromSeq === 1 && p.ends.date === first)) return "It has to end after the first session.";
    if ((seriesTotal({ ...p, firstDate: first, fromSeq }) ?? 0) > SERIES_MAX_COUNT) {
      return `A series runs for at most ${SERIES_MAX_COUNT} sessions. Pick an earlier end date.`;
    }
  }
  return null;
}

/** Is a rule one the server can keep? What is wrong, in the host's words, or null. */
export function ruleProblem(rule: SeriesRule): string | null {
  if (!parts(rule.firstDate)) return "Pick the date of the first session.";
  if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(rule.time)) return "Pick a start time.";
  if (!wallTimeToUtc(rule.firstDate, rule.time, rule.timezone)) return "Pick a time zone.";
  if (!Number.isInteger(rule.fromSeq) || rule.fromSeq < 1) return "Pick the session the change starts at.";
  return patternProblem(rule, rule.firstDate, rule.fromSeq);
}
